import { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { Modal, DatePicker, TimePicker, Text, Spacer, Advice } from 'hoi-poi-ui';
import { LottieGif, SectionList } from '@web/web5';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { AgendaActions, EntityActions } from 'actions';
import { ensureRoute } from 'utils/routes';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { formatDateToBackend, getOffsetTimeZoneValues, formatDate } from 'utils/dates';
import { errorToast } from 'utils/toast';
import { debounceContinue } from 'utils/debounce';
import { getSrcCompanyCircleAvatar, getSrcUserCircleAvatar } from 'utils/getSrcAvatar';
import { getUserSfmUrl } from 'utils/getUrl';
import { logEvent } from 'utils/tracking';

import { bulkMeetingCreation } from 'services/BulkService';

import SuccessImage from 'components/images/SuccessImage';
import Select from 'containers/components/Fields/Select';
import lottieScheduleJSON from './lottieSchedule.json';

const ModalScheduleBulk = memo(({ entity, onRef, selected }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [resultErrors, setResultErrors] = useState();
    const [finished, setFinished] = useState();

    useEffect(() => {
        onRef &&
            onRef({
                open() {
                    // Reset and defaults
                    setResultErrors();
                    setLoading(false);
                    setForm({
                        meetingStartHour: new Date(new Date().setHours(9, 0, 0, 0)),
                        slotBetweenMeetings: { label: '1h', value: '4' },
                        meetingDuration: { label: '1h', value: '4' },
                    });
                    setErrors({});
                    setFinished(false);
                    setOpen(true);

                    logEvent({
                        event: entity?.trueName,
                        submodule: 'schedule',
                        functionality: 'start',
                    });
                },
            });
    }, [entity?.trueName, onRef]);

    const onInnerCancel = useCallback(() => {
        setOpen(false);
    }, []);

    const goToCalendar = useCallback(() => {
        const newStartDate = moment(form.meetingStartDate)
            .hours(0)
            .minutes(0)
            .seconds(0)
            .milliseconds(0)
            .utc()
            .toDate();
        const newEndDate = moment(newStartDate).add(1, 'days').utc().toDate();
        const dateRange = { startDate: newStartDate, endDate: newEndDate };
        dispatch(AgendaActions.changeDateRangeFilter(dateRange, true));
        ensureRoute('/agenda');
        // Removing current selection
        dispatch(EntityActions.closeAll(entity));
    }, [dispatch, entity, form.meetingStartDate]);

    const onInnerSave = useCallback(() => {
        let newErrors = {};
        [
            'meetingType',
            'meetingStartDate',
            'meetingStartHour',
            'slotBetweenMeetings',
            'meetingDuration',
        ].forEach((field) =>
            !form[field] ? (newErrors[field] = getLiteral('helptext_required_field')) : undefined,
        );

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        const hours = form.meetingStartHour.getUTCHours().toString().padStart(2, '0');
        const minutes = form.meetingStartHour.getUTCMinutes().toString().padStart(2, '0');

        let dto = {
            data: JSON.stringify({
                entity: entity.entity,
                entityIds: selected,
                meetingType: parseInt(form.meetingType.value, 10),
                meetingStartDate: formatDateToBackend(form.meetingStartDate, 'YYYY-MM-DDTHH:mm:ss'),
                meetingStartHour: `${hours}:${minutes}`,
                meetingDuration: parseInt(form.meetingDuration.value, 10),
                slotBetweenMeetings: parseInt(form.slotBetweenMeetings.value, 10),
            }),
            utcOffset: getOffsetTimeZoneValues().utcOffset,
        };

        logEvent({
            event: entity?.trueName,
            submodule: 'schedule',
            functionality: 'end',
        });

        const start = Date.now();
        setLoading(true);
        bulkMeetingCreation(dto)
            .then((result) => {
                debounceContinue({
                    start,
                    diffTime: 2000,
                    callback: () => {
                        if (result.State === '0' && result.Result?.length) {
                            setResultErrors(
                                result.Result.map((error) => {
                                    const { src, fallbackSrc } = getSrcCompanyCircleAvatar(
                                        error.AccountId,
                                    );
                                    const ownerImage = getSrcUserCircleAvatar(error.OwnerId);
                                    return {
                                        AccountName: error.AccountName,
                                        OwnerId: error.OwnerId,
                                        OwnerName: error.OwnerName,
                                        AccountType: error.AccountType,
                                        srcAccount: src,
                                        placeholderAccount: fallbackSrc,
                                        srcResponsible: ownerImage.src,
                                        placeholderResponsible: ownerImage.fallbackSrc,
                                    };
                                }),
                            );
                        } else if (result.State === '0' && !result.Result?.length) {
                            errorToast({ text: getLiteral('error_generalerror') });
                            return;
                        }

                        setLoading(false);
                        setFinished(true);
                    },
                });
            })
            .catch((e) => {
                console.error(e);
                errorToast({ text: getLiteral('error_generalerror') });
                setLoading(false);
            });
    }, [entity?.entity, entity?.trueName, form, selected]);

    const onChange = useCallback(
        (field) => (value) => {
            setErrors({});
            setForm((current) => ({ ...current, [field]: value }));
        },
        [],
    );

    const errorsColumnsDef = useMemo(
        () => [
            {
                colId: 'AccountName',
                field: 'AccountName',
                headerName: getLiteral('label_account_name'),
                headerComponent: 'headerTextCell',
                resizable: false,
                pinned: 'left',
                cellRenderer: 'avatarCell',
                cellRendererParams: {
                    bold: true,
                    otherFields: {
                        src: 'srcAccount',
                        placeholder: 'placeholderAccount',
                    },
                },
            },
            {
                colId: 'OwnerName',
                field: 'OwnerName',
                headerName: getLiteral('label_owner'),
                headerComponent: 'headerTextCell',
                resizable: false,
                cellRenderer: 'avatarCell',
                cellRendererParams: {
                    getUrl: (data) => {
                        if (!data.OwnerId) return;
                        return getUserSfmUrl(data.OwnerId) || '';
                    },
                    otherFields: {
                        src: 'srcResponsible',
                        placeholder: 'placeholderResponsible',
                    },
                },
            },
            {
                colId: 'AccountType',
                field: 'AccountType',
                headerName: getLiteral('label_account_type'),
                headerComponent: 'headerTextCell',
                cellRenderer: 'textCell',
                resizable: false,
            },
        ],
        [],
    );

    const showForm = !loading && !finished;
    const showResultErrors = !loading && resultErrors?.length;
    const showSuccessView = !loading && finished && !resultErrors?.length;
    const showCancelBtn = !loading;
    const showConfirmBtn = !loading;
    const confirmText = finished ? getLiteral('action_go_calendar') : getLiteral('action_create');
    const cancelText = getLiteral('action_cancel');
    let onConfirm = showConfirmBtn ? (finished ? goToCalendar : onInnerSave) : undefined;

    return (
        <Modal
            className="fm-modal-schedule-bulk"
            isOpen={open}
            onRequestClose={onInnerCancel}
            onCancel={showCancelBtn && onInnerCancel}
            onConfirm={onConfirm}
            confirmText={confirmText}
            cancelText={cancelText}
            title={getLiteral('title_schedule_accounts')}
            size="large"
        >
            {loading && (
                <div className="fm-modal-schedule-bulk__creating">
                    <LottieGif width="300px" height="220px" src={lottieScheduleJSON} />
                    <Spacer y={2} />
                    <Text>
                        {getLiteralWithParameters('label_creating_events_wait', [
                            formatDate(form.meetingStartDate),
                        ])}
                    </Text>
                    <Spacer y={4} />
                </div>
            )}
            {showResultErrors && (
                <div className="fm-modal-schedule-bulk__result-errors">
                    <Advice type="error" showIcon showCollapse={false}>
                        {getLiteral('label_error_message_creating')}
                    </Advice>
                    <Spacer y={4} />
                    <SectionList
                        id="schedule-bulk__result-errors-list"
                        className="fm-modal-schedule-bulk__result-errors-list"
                        columns={errorsColumnsDef}
                        rows={resultErrors}
                        getRowNodeId={(node) => node.EntityId}
                    />
                </div>
            )}
            {showSuccessView && (
                <div className="fm-modal-schedule-bulk__success">
                    <SuccessImage />
                    <Spacer y={2} />
                    <Text type="h6">
                        {getLiteralWithParameters('success_events_created', [selected?.length])}
                    </Text>
                    <Spacer y={2} />
                    <Text>{getLiteral('success_events_created_desc')}</Text>
                    <Spacer y={4} />
                </div>
            )}
            {showForm && (
                <div className="fm-modal-schedule-bulk__form">
                    <Select
                        label={getLiteral('label_schedule_type')}
                        list="tblTiposCalendario"
                        onChange={onChange('meetingType')}
                        value={form.meetingType}
                        error={errors.meetingType}
                        mandatory
                    />
                    <DatePicker
                        label={getLiteral('label_date')}
                        onChange={onChange('meetingStartDate')}
                        value={form.meetingStartDate}
                        error={errors.meetingStartDate}
                        isFullWidth
                        isRequired
                    />
                    <TimePicker
                        label={getLiteral('label_time_first_meeting')}
                        hint={getLiteral('label_time_first_meeting_info')}
                        onChange={onChange('meetingStartHour')}
                        value={form.meetingStartHour}
                        error={errors.meetingStartHour}
                        isFullWidth
                        isRequired
                    />
                    <Select
                        label={getLiteral('label_between_meetings')}
                        list="tblBulkMeetingMinutes"
                        onChange={onChange('slotBetweenMeetings')}
                        value={form.slotBetweenMeetings}
                        error={errors.slotBetweenMeetings}
                        mandatory
                    />
                    {form.meetingType?.value !== '2' && (
                        <Select
                            label={getLiteral('label_meet_duration')}
                            list="tblBulkMeetingMinutes"
                            onChange={onChange('meetingDuration')}
                            value={form.meetingDuration}
                            error={errors.meetingDuration}
                            mandatory
                        />
                    )}
                </div>
            )}
        </Modal>
    );
});

export default ModalScheduleBulk;
